import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { currentLocale, scopedTranslation } from '@utils/I18n'

import { eventsNear, type FoundPlace } from '@utils/embeds/distance'
import type { EmbedConfig, Layout, Radius, View } from '@utils/embeds/embedConfig'
import type { EventsFeed, FeedStatus } from '@utils/embeds/eventsFeed'
import { mapPins } from '@utils/embeds/mapPins'
import { groupSeries } from '@utils/embeds/series'
import { offersLocationSearch, type PublicEvent } from '@utils/embeds/publicEvent'
import {
  NO_CHOICES,
  activeChoices,
  applyChoices,
  filterControls,
  hasChoices,
  type SupporterChoices,
} from '@utils/embeds/supporterFilters'
import { loadFont, setFont } from '@utils/embeds/fonts'
import { CORNER_RADII, embedTheme } from '@utils/embeds/theme'
import { EmbedButton } from './EmbedButton'
import { EmbedEmptyState } from './EmbedEmptyState'
import { EventCard } from './EventCard'
import { EventsMap } from './EventsMap'
import { EventStrip } from './EventStrip'
import { LocationSearch, type LocationSearchHandle } from './LocationSearch'
import { NearestEventNotice, NoEventsNearby } from './NothingNearby'
import { SupporterFilters } from './SupporterFilters'
import { ViewSwitch } from './ViewSwitch'

const t = scopedTranslation('embeds.events')

const PAGE_SIZE = 6

const PHONE_MAP_MIN_HEIGHT = 384
const HOST_HEADER_ROOM = 96

type EventsEmbedProps = {
  headingLevel?: 1 | 2
  config: EmbedConfig
  brand: string
  feed: Pick<EventsFeed, 'events' | 'status' | 'retry'>
  country: string
  googleMapsApiKey: string | null
  screenHeight?: number | null
}

type SizeClass = 'sm' | 'md' | 'lg'

type PhonePane = 'list' | 'map'

function useSizeClass<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(1000)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(() => setWidth(element.getBoundingClientRect().width))
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const size: SizeClass = width < 560 ? 'sm' : width < 900 ? 'md' : 'lg'
  return { ref, size }
}

type Screen = Exclude<FeedStatus, 'ready'> | 'no_events' | 'no_matches' | 'events'

function screenFor({
  status,
  eventCount,
  matchCount,
}: {
  status: FeedStatus
  eventCount: number
  matchCount: number
}): Screen {
  if (status !== 'ready') return status
  if (eventCount === 0) return 'no_events'
  if (matchCount === 0) return 'no_matches'
  return 'events'
}

const GRID_COLUMNS: Record<SizeClass, string> = { sm: 'tw:grid-cols-1', md: 'tw:grid-cols-2', lg: 'tw:grid-cols-3' }

function SkeletonCard() {
  return (
    <div className="tw:flex tw:animate-pulse tw:gap-3 tw:rounded-(--embed-radius,1rem) tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:motion-reduce:animate-none">
      <div className="tw:h-14 tw:w-12 tw:rounded-(--embed-radius-sm,0.5rem) tw:bg-gray-200" />
      <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-2">
        <div className="tw:h-3 tw:w-1/4 tw:rounded tw:bg-gray-200" />
        <div className="tw:h-4 tw:w-3/4 tw:rounded tw:bg-gray-200" />
        <div className="tw:h-3 tw:w-2/3 tw:rounded tw:bg-gray-200" />
        <div className="tw:h-3 tw:w-1/2 tw:rounded tw:bg-gray-200" />
      </div>
    </div>
  )
}

export function EventsEmbed({
  headingLevel = 1,
  config,
  brand,
  feed,
  country,
  googleMapsApiKey,
  screenHeight = null,
}: EventsEmbedProps) {
  const { status } = feed
  const { ref, size } = useSizeClass<HTMLDivElement>()
  const theme = useMemo(() => embedTheme(brand), [brand])
  const corners = CORNER_RADII[config.corners]
  const locale = `${currentLocale()}-${country.toUpperCase()}`
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [revealedFrom, setRevealedFrom] = useState<number | null>(null)
  const [place, setPlace] = useState<FoundPlace | null>(null)
  const [chosenRadius, setChosenRadius] = useState<Radius | null>(null)
  const radius = chosenRadius ?? config.radius
  const [chosenView, setChosenView] = useState<View | null>(null)
  const [phonePane, setPhonePane] = useState<PhonePane>('list')
  const [mapFailed, setMapFailed] = useState(false)
  const [highlightedId, setHighlightedId] = useState<number | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [choices, setChoices] = useState<SupporterChoices>(NO_CHOICES)
  const [now] = useState(() => new Date())
  const searchRef = useRef<LocationSearchHandle>(null)
  const focusSwitch = useRef(false)
  const listRef = useRef<HTMLDivElement>(null)
  const onlineHeadingId = useId()
  const Title = `h${headingLevel}` as const
  const sectionLevel = headingLevel === 1 ? 2 : 3
  const Section = `h${sectionLevel}` as const

  useEffect(() => loadFont(config.font), [config.font])
  useLayoutEffect(() => {
    if (ref.current) setFont(ref.current, config.font)
  }, [ref, config.font])

  const controls = useMemo(() => filterControls({ events: feed.events, config }), [feed.events, config])
  const active = activeChoices(choices, controls)
  const choosing = hasChoices(active)
  const { events, laterDates } = groupSeries(applyChoices({ events: feed.events, choices: active, now }))

  const screen = screenFor({ status, eventCount: feed.events.length, matchCount: events.length })
  const inPerson = events.filter((event) => !event.online)
  const online = events.filter((event) => event.online)

  const searchable =
    offersLocationSearch({ googleMapsApiKey, feed }) &&
    (screen === 'loading' || screen === 'events' || screen === 'no_matches')
  const search =
    screen === 'events' && searchable && place && !active.onlineOnly
      ? eventsNear({ events: inPerson, place, radius })
      : null
  const nearestToShow = search && search.nearby.length === 0 && config.showNearest ? search.nearest : null

  const mainItems: Array<{ event: PublicEvent; distance?: number }> = !search
    ? (inPerson.length > 0 ? inPerson : online).map((event) => ({ event }))
    : nearestToShow
      ? [nearestToShow]
      : search.nearby
  const onlineItems = search || inPerson.length > 0 ? online : []
  const nothingNearby = search !== null && mainItems.length === 0

  const mapsKey = offersLocationSearch({ googleMapsApiKey, feed }) ? googleMapsApiKey : null
  const mappable = mapsKey !== null && !mapFailed && mapPins(feed.events.filter((event) => !event.online)).length > 0
  const layout: Layout = mappable ? config.layout : 'grid'
  const view: View = layout === 'both' ? (chosenView ?? config.defaultView) : layout
  const mainEvents = mainItems.map(({ event }) => event)
  const pins = mapPins(mainEvents)
  const failMap = useCallback(() => setMapFailed(true), [])

  const selectEvent = (id: number | null) => {
    setSelectedId(id)
    const index = mainItems.findIndex(({ event }) => event.id === id)
    if (index >= visible) setVisible(index + 1)
  }

  useEffect(() => {
    const list = listRef.current
    const card = list?.querySelector<HTMLElement>(`[data-event-id="${selectedId}"]`)
    if (!list || !card) return
    const top = card.getBoundingClientRect().top - list.getBoundingClientRect().top
    const bottom = top + card.offsetHeight
    const padding = parseFloat(getComputedStyle(list).paddingTop)
    if (top < 0 || bottom > list.clientHeight) list.scrollTop += top - padding
  }, [selectedId])

  const showFirstPage = () => {
    setVisible(PAGE_SIZE)
    setRevealedFrom(null)
  }
  const choosePlace = (next: FoundPlace | null) => {
    setPlace(next)
    showFirstPage()
  }
  const changeRadius = (next: Radius) => {
    setChosenRadius(next)
    showFirstPage()
  }
  const choose = (next: SupporterChoices) => {
    setChoices(next)
    showFirstPage()
  }
  const showAll = () => {
    choosePlace(null)
    searchRef.current?.focus()
  }

  const heading = config.heading ?? (searchable ? t('search.heading') : t('heading'))
  const rsvpLabel = config.cta ?? t('rsvp')
  const cardSettings = {
    links: config.links,
    rsvpLabel,
    showCoverage: config.shifts,
    showEventType: config.showEventType,
    showIntro: config.showIntro,
    locale,
  }

  const statusText = (() => {
    switch (screen) {
      case 'loading':
        return t('finding_events')
      case 'error':
        return t('error.title')
      case 'no_events':
        return t('no_events.title')
      case 'no_matches':
        return t('no_matches.title')
      case 'events': {
        if (!search) return t(choosing ? 'showing_matching_events' : 'showing_events', { count: events.length })
        const where = { radius: search.radius, place: search.place }
        if (search.nearby.length > 0) return t('search.events_within', { count: search.nearby.length, ...where })
        return nearestToShow ? t('search.nearest_event_to', where) : t('search.no_events_within', where)
      }
    }
  })()
  const statusShownElsewhere = screen === 'error' || screen === 'no_events' || screen === 'no_matches' || nothingNearby

  const choosePhonePane = (pane: PhonePane) => {
    setPhonePane(pane)
    focusSwitch.current = true
  }

  useEffect(() => {
    if (!focusSwitch.current) return
    focusSwitch.current = false
    ref.current?.querySelector<HTMLElement>('[role="group"] [aria-pressed="true"]')?.focus()
  }, [phonePane, ref])

  const switchOverMap = layout === 'map' && size === 'sm' && phonePane === 'map'
  const viewSwitch =
    screen !== 'events' ? null : layout === 'both' ? (
      <ViewSwitch<View>
        ariaLabel={t('view.label')}
        value={view}
        onChange={setChosenView}
        options={[
          { value: 'grid', label: t('view.grid'), leadingIcon: 'grid-01' },
          { value: 'map', label: t('view.map'), leadingIcon: 'map-01' },
        ]}
      />
    ) : layout === 'map' && size === 'sm' ? (
      <ViewSwitch<PhonePane>
        ariaLabel={t('view.label')}
        value={phonePane}
        onChange={choosePhonePane}
        options={[
          { value: 'list', label: t('view.list'), leadingIcon: 'dot-points' },
          { value: 'map', label: t('view.map'), leadingIcon: 'map-01' },
        ]}
      />
    ) : null

  const eventList = ({ columns, pointable }: { columns: string; pointable: boolean }) => {
    const pointAt = (id: number) =>
      pointable
        ? {
            onPointerEnter: () => setHighlightedId(id),
            onPointerLeave: () => setHighlightedId(null),
            onFocus: () => setHighlightedId(id),
            onBlur: () => setHighlightedId(null),
          }
        : {}

    return (
      <div className="tw:flex tw:flex-col tw:gap-4">
        {search && nearestToShow && (
          <NearestEventNotice search={search} locale={locale} onWiden={changeRadius} onShowAll={showAll} />
        )}
        {search && nothingNearby && (
          <NoEventsNearby
            search={search}
            headingLevel={sectionLevel}
            hasOnlineEvents={online.length > 0}
            locale={locale}
            onWiden={changeRadius}
            onShowAll={showAll}
          />
        )}
        {mainItems.length > 0 && (
          <div className={cn('tw:grid tw:gap-4', columns)}>
            {mainItems.slice(0, visible).map(({ event, distance }, index) => (
              <div
                key={event.id}
                data-event-id={event.id}
                aria-current={event.id === selectedId || undefined}
                className={cn(
                  'tw:shrink-0 tw:rounded-[var(--embed-radius,1rem)]',
                  event.id === selectedId && 'tw:ring-2 tw:ring-[color:var(--embed-brand)]'
                )}
                {...pointAt(event.id)}
              >
                <EventCard
                  headingLevel={sectionLevel}
                  event={event}
                  distance={distance}
                  laterDates={laterDates.get(event.id)}
                  {...cardSettings}
                  autoFocus={index === revealedFrom}
                />
              </div>
            ))}
          </div>
        )}
        {mainItems.length > visible && (
          <div className="tw:flex tw:justify-center">
            <EmbedButton
              color="neutral"
              outline
              onClick={() => {
                setRevealedFrom(visible)
                setVisible(visible + PAGE_SIZE)
              }}
            >
              {t('show_more', { count: mainItems.length - visible })}
            </EmbedButton>
          </div>
        )}
        {onlineItems.length > 0 && (
          <section className="tw:flex tw:flex-col tw:gap-3 tw:pt-2" aria-labelledby={onlineHeadingId}>
            <Section
              id={onlineHeadingId}
              className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:text-gray-800"
            >
              <Icon type="message-chat-square" className="tw:h-4 tw:w-4 tw:text-gray-500" />
              {search ? t('search.online_events') : t('online_events')}
            </Section>
            <div className={cn('tw:grid tw:gap-4', columns)}>
              {onlineItems.map((event) => (
                <EventCard
                  key={event.id}
                  headingLevel={(sectionLevel + 1) as 3 | 4}
                  event={event}
                  laterDates={laterDates.get(event.id)}
                  {...cardSettings}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    )
  }

  const mapView = () => {
    const map = mapsKey && pins.length > 0 && (
      <EventsMap
        pins={pins}
        pinColor={config.pin ?? theme.brand}
        highlightedId={highlightedId}
        selectedId={selectedId}
        onSelect={selectEvent}
        links={config.links}
        rsvpLabel={rsvpLabel}
        showCoverage={config.shifts}
        locale={locale}
        apiKey={mapsKey}
        country={country}
        onError={failMap}
        phone={size === 'sm'}
      />
    )
    const list = (scrolls: boolean) => (
      <div ref={scrolls ? listRef : undefined} className={cn(scrolls && 'tw:relative tw:min-h-0 tw:overflow-y-auto tw:p-1')}>
        {eventList({ columns: 'tw:grid-cols-1', pointable: Boolean(map) })}
      </div>
    )

    if (!map) return list(false)
    if (size === 'lg')
      return (
        <div className="tw:grid tw:h-[600px] tw:grid-cols-[minmax(320px,2fr)_minmax(0,3fr)] tw:grid-rows-[minmax(0,1fr)] tw:gap-4">
          {list(true)}
          {map}
        </div>
      )
    if (size === 'md')
      return (
        <div className="tw:grid tw:h-[720px] tw:grid-rows-[18rem_minmax(0,1fr)] tw:gap-4">
          {map}
          {list(true)}
        </div>
      )
    if (layout === 'map' && phonePane === 'list') return list(false)
    const phoneMapHeight = screenHeight && Math.max(PHONE_MAP_MIN_HEIGHT, screenHeight - HOST_HEADER_ROOM)
    return (
      <div className="tw:flex tw:flex-col" style={phoneMapHeight ? { height: phoneMapHeight } : undefined}>
        <div className={cn('tw:relative', phoneMapHeight ? 'tw:min-h-0 tw:flex-1' : 'tw:h-96')}>
          {map}
          {switchOverMap && (
            <div className="tw:absolute tw:bottom-8 tw:left-1/2 tw:-translate-x-1/2 tw:rounded-[var(--embed-radius-sm,0.5rem)] tw:shadow-lg">
              {viewSwitch}
            </div>
          )}
        </div>
        <EventStrip
          events={mainEvents}
          selectedId={selectedId}
          onSelect={selectEvent}
          links={config.links}
          rsvpLabel={rsvpLabel}
          showCoverage={config.shifts}
          locale={locale}
        />
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className="tw:flex tw:w-full tw:flex-col tw:gap-4 tw:bg-white tw:p-1 tw:text-gray-900"
      style={
        {
          '--embed-brand': theme.brand,
          '--embed-brand-text': theme.brandText,
          '--embed-brand-ink': theme.brandInk,
          '--embed-brand-soft': theme.brandSoft,
          '--embed-radius': corners.radius,
          '--embed-radius-sm': corners.radiusSm,
        } as CSSProperties
      }
    >
      <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-(--embed-radius,1rem) tw:bg-(--embed-brand-soft) tw:p-4">
        <Title className="tw:text-lg tw:font-bold tw:text-gray-900">{heading}</Title>
        {searchable && googleMapsApiKey && (
          <LocationSearch
            ref={searchRef}
            label={heading}
            placeholder={config.placeholder ?? t('search.placeholder')}
            unavailableMessage={t('search.unavailable')}
            failedMessage={t('search.search_failed')}
            country={country}
            googleMapsApiKey={googleMapsApiKey}
            place={place}
            onPlaceChange={choosePlace}
            radius={radius}
            onRadiusChange={changeRadius}
            stacked={size === 'sm'}
          />
        )}
      </div>
      {(screen === 'events' || screen === 'no_matches') && (
        <SupporterFilters controls={controls} choices={choices} onChange={choose} clearable={choosing} />
      )}
      <div
        className={cn(
          'tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3',
          !statusShownElsewhere && 'tw:border-b tw:border-gray-200 tw:pb-3'
        )}
      >
        <p
          className={cn('tw:text-sm tw:font-semibold tw:text-gray-800', statusShownElsewhere && 'tw:sr-only')}
          role="status"
        >
          {statusText}
        </p>
        {!switchOverMap && viewSwitch}
      </div>
      {mapFailed && config.layout !== 'grid' && (
        <p
          role="status"
          className="tw:flex tw:items-center tw:gap-2 tw:rounded-(--embed-radius-sm,0.5rem) tw:bg-gray-100 tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700"
        >
          <Icon type="alert-triangle" className="tw:h-4 tw:w-4 tw:shrink-0" />
          {t('map.failed')}
        </p>
      )}
      {screen === 'loading' && (
        <div className={cn('tw:grid tw:gap-4', GRID_COLUMNS[size])}>
          {Array.from({ length: size === 'sm' ? 2 : 3 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      )}
      {screen === 'error' && (
        <EmbedEmptyState
          headingLevel={sectionLevel}
          icon="alert-circle"
          title={t('error.title')}
          action={<EmbedButton onClick={feed.retry}>{t('error.retry')}</EmbedButton>}
        >
          <p>{t('error.body')}</p>
        </EmbedEmptyState>
      )}
      {screen === 'no_events' && (
        <EmbedEmptyState headingLevel={sectionLevel} icon="calendar" title={t('no_events.title')}>
          <p>{t('no_events.body')}</p>
        </EmbedEmptyState>
      )}
      {screen === 'no_matches' && (
        <EmbedEmptyState
          headingLevel={sectionLevel}
          icon="filter-lines"
          title={t('no_matches.title')}
          action={<EmbedButton onClick={() => choose(NO_CHOICES)}>{t('no_matches.clear')}</EmbedButton>}
        >
          <p>{t('no_matches.body')}</p>
        </EmbedEmptyState>
      )}
      {screen === 'events' &&
        (view === 'grid' ? eventList({ columns: GRID_COLUMNS[size], pointable: false }) : mapView())}
    </div>
  )
}
